import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  TimeoutError,
  catchError,
  combineLatest,
  filter,
  first,
  map,
  of,
  tap,
  timeout,
} from 'rxjs';
import { ProfileState } from '../state/profile.state';

/**
 * Blocks app access for a user whose Profile-v2-owned `requiredMfaMethod` is
 * `'Authenticator'` until they've actually completed real TOTP enrollment.
 * Enrollment status is Better Auth's own live `user.twoFactorEnabled`, populated
 * into the store by `SetTwoFactorEnabled` right after login (see
 * `AppComponent.bootstrapBetterAuth` in apps/escrowtab and apps/admin) — this is
 * deliberately not read from `SelfProfile`, which only carries the *policy*
 * (`requiredMfaMethod`), not whether enrollment actually happened.
 *
 * `Email`/`Sms` requirements need no gate here: Profile v2 already force-sets
 * `twoFactorEnabled=true` server-side for those, so the normal login OTP
 * challenge already covers them — this guard only exists for the
 * `Authenticator` case, where the server intentionally leaves
 * `twoFactorEnabled=false` until the user has actually scanned a QR code.
 *
 * This is a client-side-only gate — see the "Known limitation" section of the
 * mandatory-2FA-enrollment plan for why a pre-existing, unrevoked access token
 * can still bypass it against resource-server APIs directly. Revoking sessions
 * on any `requiredMfaMethod` change (done server-side in Profile v2) closes most
 * of that gap in practice.
 *
 * Waits for `selfProfile` to be populated before deciding (mirrors
 * `etAdminGuard`'s wait-for-load pattern — both are dispatched asynchronously
 * after the post-login API calls resolve, which typically lands after this guard
 * first runs). When `requiredMfaMethod !== 'Authenticator'`, only `selfProfile`
 * needs to resolve; otherwise `twoFactorEnabled` must resolve too.
 */
export const mfaRequiredGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const store = inject(Store);
  return combineLatest([
    store.select(ProfileState.getSelfProfile),
    store.select(ProfileState.getTwoFactorEnabled),
  ]).pipe(
    map(([selfProfile, twoFactorEnabled]) => {
      if (!selfProfile) {
        return undefined;
      }
      if (selfProfile.requiredMfaMethod !== 'Authenticator') {
        return true;
      }
      return twoFactorEnabled === undefined ? undefined : twoFactorEnabled;
    }),
    filter((allowed): allowed is boolean => allowed !== undefined),
    tap((allowed) => {
      if (!allowed) {
        router.navigate(['/auth/two-factor-required'], {
          queryParams: { returnUrl: state.url },
        });
      }
    }),
    first(),
    timeout({ first: 15_000 }),
    catchError((err) => {
      if (err instanceof TimeoutError) {
        router.navigate(['/auth/login'], {
          queryParams: { error: 'session_timeout' },
        });
      }
      return of(false);
    }),
  );
};
